import React, { useState, useEffect } from 'react';
import { postContactInfo } from '../../../hooks/api-methods/post-contact-info';
import { updateContact } from '../../../hooks/api-methods/put-contact-info';
import { getContactInfo } from '../../../hooks/api-methods/get-contact-info';
import ButtonSec from '../../../components/Buttons/ButtonSecundary';

interface ContactInfo {
  phone: string;
  email: string;
  address: string;
  vat: string;
  mapsUrl: string;
}

interface ContactInfoFormProps {
  onSave?: (data: ContactInfo) => void;
}

const ContactInfoForm: React.FC<ContactInfoFormProps> = ({ onSave }) => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '',
    email: '',
    address: '',
    vat: '',
    mapsUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isUpdate, setIsUpdate] = useState(false); // 👈 new flag

  // Fetch existing contact info on mount
  useEffect(() => {
    async function fetchContact() {
      try {
        const data = await getContactInfo();
        if (data) {
          setContactInfo({
            phone: data.phone || '',
            email: data.email || '',
            address: data.adress || '',
            vat: data.vat || '',
            mapsUrl: data.mapsUrl || '',
          });
          setIsUpdate(true); // 👈 if data exists, we’ll update instead of post
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchContact();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const payload = {
      id: 'contactInfo', // your Cosmos DB document ID
      phone: contactInfo.phone,
      email: contactInfo.email,
      adress: contactInfo.address,
      vat: contactInfo.vat,
      mapsUrl: contactInfo.mapsUrl,
    };

    try {
      if (isUpdate) {
        await updateContact(payload);
        setMessage('Contact info updated successfully!');
      } else {
        await postContactInfo(payload);
        setMessage('Contact info created successfully!');
        setIsUpdate(true);
      }
      if (onSave) onSave(contactInfo);
    } catch (err) {
      console.error('Error saving contact info:', err);
      setMessage('Failed to save contact info.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading contact info...</p>;

  return (
    <div className="contact-form">
      <form onSubmit={handleSubmit} className="grid gap-1">
        <div className="colomn-6 colomn-small-12">
          <label htmlFor="phone">Telefoonnummer</label>
          <input
            id="phone"
            name="phone"
            type="tel"
            placeholder="bv. +32 470 12 34 56"
            value={contactInfo.phone}
            onChange={handleChange}
            className="rounded-input w-100"
          />
        </div>

        <div className="colomn-6 colomn-small-12">
          <label htmlFor="email">E-mailadres</label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="bv. info@bedrijf.be"
            value={contactInfo.email}
            onChange={handleChange}
            className="rounded-input w-100"
          />
        </div>

        <div className="colomn-12">
          <label htmlFor="address">Adres</label>
          <input
            id="address"
            name="address"
            type="text"
            placeholder="bv. https url "
            value={contactInfo.address}
            onChange={handleChange}
            className="rounded-input w-100"
          />
        </div>
        <div className="colomn-12">
          <label htmlFor="mapsUrl">Google maps url</label>
          <input
            id="mapsUrl"
            name="mapsUrl"
            type="text"
            placeholder="bv. Hoofdstraat 10, 1000 Brussel"
            value={contactInfo.mapsUrl}
            onChange={handleChange}
            className="rounded-input w-100"
          />
        </div>

        <div className="colomn-6 colomn-small-12">
          <label htmlFor="vat">BTW-nummer</label>
          <input
            id="vat"
            name="vat"
            type="text"
            placeholder="bv. BE0123456789"
            value={contactInfo.vat}
            onChange={handleChange}
            className="rounded-input w-100"
          />
        </div>

        {message && <p className="colomn-12">{message}</p>}

        <div className="colomn-12 flex-row justify-end">
          <ButtonSec text={saving ? 'Opslaan...' : 'Opslaan'} type="submit" disabled={saving} />
        </div>
      </form>
    </div>
  );
};

export default ContactInfoForm;
